import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as TalentIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { talentService } from '../services/talentService';

const Talents = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for talents data
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // State for table functionality
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');

  // State for search and filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [talentCategories, setTalentCategories] = useState([]);

  // State for delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, talent: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTalents();
    loadTalentCategories();
  }, []);

  const loadTalentCategories = async () => {
    try {
      const categories = await talentService.getTalentCategories();
      setTalentCategories(categories);
      // Default to all categories selected to show "All Categories"
      setSelectedCategories(categories);
    } catch (err) {
      console.error('Error loading talent categories:', err);
    }
  };

  const loadTalents = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    
    try {
      talentService.validateConfiguration();
      const talentsData = await talentService.getTalents();
      setTalents(talentsData);
      console.log('Talents loaded successfully:', talentsData.length);
    } catch (err) {
      console.error('Error loading talents:', err);
      if (err.message.includes('Missing required environment variables')) {
        setError(`Configuration Error: ${err.message}. Please check your .env file.`);
      } else if (err.message.includes('Failed to fetch talents: 404') || err.message.includes('Invalid JSON response')) {
        console.log('Talents file not found or invalid, attempting to initialize...');
        try {
          const initializedTalents = await talentService.initializeTalentsFile();
          setTalents(initializedTalents);
          setError(''); // Clear any error since we successfully initialized
        } catch (initError) {
          console.error('Failed to initialize talents file:', initError);
          setError(`Failed to initialize talents: ${initError.message}. Please ensure the talents.json file exists in your blob storage with content: []`);
        }
      } else {
        setError(`Failed to load talents: ${err.message}`);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleCreateTalent = () => {
    navigate('/talent/new');
  };

  const handleEditTalent = (talentId) => {
    navigate(`/talent/${talentId}`);
  };

  const handleDeleteClick = (talent) => {
    setDeleteDialog({ open: true, talent });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.talent) return;

    setDeleting(true);
    try {
      await talentService.deleteTalent(deleteDialog.talent.talentId);
      await loadTalents();
      setDeleteDialog({ open: false, talent: null });
    } catch (err) {
      console.error('Error deleting talent:', err);
      setError(`Failed to delete talent: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, talent: null });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCategoryFilterChange = (event) => {
    const value = event.target.value;
    if (value.includes('All')) {
      // Toggle between all categories and no categories
      if (selectedCategories.length === talentCategories.length) {
        // If all categories are currently selected, deselect all
        setSelectedCategories([]);
      } else {
        // If not all categories are selected, select all
        setSelectedCategories(talentCategories);
      }
    } else {
      // Otherwise, use the selected categories
      setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
    }
    setPage(0);
  };

  // Filter and sort talents
  const filteredAndSortedTalents = React.useMemo(() => {
    let filtered = talents.filter(talent => {
      const fullName = `${talent.firstName} ${talent.lastName}`.toLowerCase();
      const searchMatch = fullName.includes(searchText.toLowerCase());
      
      const statusMatch = !statusFilter || talent.status === statusFilter;
      
      let categoryMatch = true;
      if (selectedCategories.length > 0) {
        if (selectedCategories.length === talentCategories.length) {
          // "All" is selected - include talents with no category
          categoryMatch = true;
        } else {
          // Specific categories selected - only include talents with those categories
          const talentCats = talentService.formatCategoriesForDisplay(talent.talentCategory);
          categoryMatch = selectedCategories.some(selectedCat => 
            talentCats.some(talentCat => talentCat === selectedCat)
          );
        }
      } else {
        // No categories selected - show no talents
        categoryMatch = false;
      }
      
      return searchMatch && statusMatch && categoryMatch;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'name') {
        aValue = `${a.firstName} ${a.lastName}`;
        bValue = `${b.firstName} ${b.lastName}`;
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [talents, searchText, statusFilter, selectedCategories, sortField, sortDirection, talentCategories]);

  const paginatedTalents = filteredAndSortedTalents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pr: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <TalentIcon sx={{ mr: 2 }} />
            Talent Pool ({filteredAndSortedTalents.length})
          </Typography>
          {refreshing && (
            <Typography variant="body2" color="text.secondary">
              Refreshing data...
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => loadTalents(true)}
            disabled={loading || refreshing}
            title="Refresh data"
            sx={{ 
              color: 'primary.main',
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              }
            }}
          >
            <RefreshIcon />
          </IconButton>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTalent}
            size={isMobile ? 'small' : 'medium'}
          >
            Add Talent
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            }}
            sx={{ minWidth: isMobile ? '100%' : 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={selectedCategories}
              onChange={handleCategoryFilterChange}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) => 
                selected.length === 0 
                  ? 'All Categories'
                  : selected.length === talentCategories.length 
                    ? 'All Categories' 
                    : `${selected.length} selected`
              }
            >
              <MenuItem value="All">
                <Checkbox 
                  checked={selectedCategories.length === talentCategories.length}
                  indeterminate={selectedCategories.length > 0 && selectedCategories.length < talentCategories.length}
                />
                <ListItemText primary="All Categories" />
              </MenuItem>
              {talentCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  <Checkbox checked={selectedCategories.indexOf(category) > -1} />
                  <ListItemText primary={category} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Name
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </TableSortLabel>
                </TableCell>
                <TableCell>Categories</TableCell>
                {!isMobile && <TableCell>Rate Range</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTalents.map((talent) => (
                <TableRow key={talent.talentId} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {talent.firstName} {talent.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction={isMobile ? "column" : "row"} spacing={0.5} flexWrap="wrap">
                      {talentService.formatCategoriesForDisplay(talent.talentCategory).map((category, index) => (
                        <Chip
                          key={index}
                          label={category}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={isMobile ? { mb: 0.5 } : {}}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {talent.rateRange ? (
                          talent.rateRange.split('|').filter(range => range.trim() !== '').map((range, index) => (
                            <Chip
                              key={index}
                              label={range.trim()}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ mb: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No rate ranges
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditTalent(talent.talentId)}
                      title="Edit Talent"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(talent)}
                      title="Delete Talent"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedTalents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No talents found. {searchText || statusFilter ? 'Try adjusting your filters.' : 'Click "Add Talent" to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={filteredAndSortedTalents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteDialog.talent?.firstName} {deleteDialog.talent?.lastName}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Talents;
